import React, { useRef, useState, useEffect } from "react";
import { FaPlay, FaVolumeUp, FaLock } from "react-icons/fa";

interface Props {
  audioUrl: string;
}

const HorenAudioPlayer: React.FC<Props> = ({ audioUrl }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [hasPlayed, setHasPlayed] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef<HTMLAudioElement>(null);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const handlePlay = () => {
    if (!audioRef.current || hasPlayed || hasStarted) return;

    setHasStarted(true);
    audioRef.current.play().catch((err) => {
      console.error("Failed to play audio:", err);
      alert("Fehler beim Abspielen des Audios. Bitte versuchen Sie es erneut.");
      setHasStarted(false);
    });
  };

  useEffect(() => {
    if (!audioRef.current) return;

    const audio = audioRef.current;

    const handleLoadedMetadata = () => {
      setDuration(audio.duration);
    };

    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
    };

    const handlePlayEvent = () => {
      setIsPlaying(true);
    };

    const handlePauseEvent = () => {
      if (hasStarted && !hasPlayed && audio.currentTime < audio.duration) {
        audio.play().catch((err) => {
          console.error("Failed to resume audio:", err);
        });
      }
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setHasPlayed(true);
    };

    const handleSeeking = (e: Event) => {
      if (hasStarted && !hasPlayed) {
        e.preventDefault();
        const validTime = currentTime;
        audio.currentTime = validTime;
      }
    };

    const handleSeeked = (e: Event) => {
      if (hasStarted && !hasPlayed) {
        e.preventDefault();
      }
    };

    audio.addEventListener("loadedmetadata", handleLoadedMetadata);
    audio.addEventListener("timeupdate", handleTimeUpdate);
    audio.addEventListener("play", handlePlayEvent);
    audio.addEventListener("pause", handlePauseEvent);
    audio.addEventListener("ended", handleEnded);
    audio.addEventListener("seeking", handleSeeking);
    audio.addEventListener("seeked", handleSeeked);

    return () => {
      audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
      audio.removeEventListener("timeupdate", handleTimeUpdate);
      audio.removeEventListener("play", handlePlayEvent);
      audio.removeEventListener("pause", handlePauseEvent);
      audio.removeEventListener("ended", handleEnded);
      audio.removeEventListener("seeking", handleSeeking);
      audio.removeEventListener("seeked", handleSeeked);
    };
  }, [hasStarted, hasPlayed, currentTime]);

  useEffect(() => {
    if (!isPlaying || hasPlayed) return;

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue =
        "Das Audio wird gerade abgespielt. Wenn Sie die Seite verlassen, können Sie es nicht erneut abspielen.";
      return e.returnValue;
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [isPlaying, hasPlayed]);

  return (
    <div className="space-y-3 sm:space-y-4">
      {/* Warning Banner - Responsive */}
      <div className="bg-red-50 border-2 border-red-300 rounded-lg sm:rounded-xl p-3 sm:p-4">
        <p className="text-red-900 font-bold text-center text-sm sm:text-base md:text-lg">
          ⚠️ ACHTUNG: Dieses Audio kann nur EINMAL abgespielt werden!
        </p>
        <div className="text-red-800 text-center text-xs sm:text-sm mt-2 space-y-1">
          <div>✗ Keine Pause möglich während der Wiedergabe</div>
          <div>✗ Kein Vor- oder Zurückspulen möglich</div>
          <div>✗ Kann nicht wiederholt werden</div>
        </div>
      </div>

      {/* Audio Player - Responsive */}
      <div className="bg-white rounded-lg sm:rounded-xl p-4 sm:p-6 border-2 border-indigo-200 shadow-lg">
        <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
          {/* Play Button - Responsive Size */}
          <button
            onClick={handlePlay}
            disabled={hasPlayed || hasStarted}
            className={`w-20 h-20 sm:w-24 sm:h-24 rounded-full flex items-center justify-center transition-all duration-300 shadow-lg flex-shrink-0 ${
              hasPlayed
                ? "bg-gray-400 cursor-not-allowed"
                : hasStarted
                ? "bg-gradient-to-r from-red-500 to-pink-500 animate-pulse cursor-not-allowed"
                : "bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 scale-100 hover:scale-110"
            }`}
          >
            {hasPlayed ? (
              <FaLock className="text-white text-3xl sm:text-4xl" />
            ) : hasStarted ? (
              <FaVolumeUp className="text-white text-3xl sm:text-4xl animate-pulse" />
            ) : (
              <FaPlay className="text-white text-3xl sm:text-4xl ml-1" />
            )}
          </button>

          {/* Progress Info - Responsive */}
          <div className="flex-1 w-full">
            <div className="flex items-start gap-2 sm:gap-3 mb-3">
              <FaVolumeUp className="text-indigo-600 text-xl sm:text-2xl flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="font-bold text-gray-900 text-sm sm:text-base md:text-lg truncate">
                  {hasPlayed
                    ? "✓ Bereits abgespielt"
                    : isPlaying
                    ? "▶ Wird abgespielt..."
                    : hasStarted
                    ? "⏳ Lädt..."
                    : "Bereit zum Abspielen"}
                </p>
                <p className="text-xs sm:text-sm text-gray-600 mt-1">
                  {hasPlayed
                    ? "Kann nicht erneut abgespielt werden"
                    : isPlaying
                    ? "Audio läuft - Keine Pause möglich"
                    : "Klicken Sie auf Play"}
                </p>
              </div>
            </div>

            {/* Progress Bar - Responsive */}
            {duration > 0 && (
              <div className="space-y-2">
                <div className="w-full bg-gray-200 rounded-full h-3 sm:h-4 overflow-hidden border border-gray-300 relative">
                  <div
                    className={`h-3 sm:h-4 rounded-full transition-all duration-300 ${
                      isPlaying
                        ? "bg-gradient-to-r from-indigo-500 to-purple-500 animate-pulse"
                        : "bg-gradient-to-r from-gray-400 to-gray-500"
                    }`}
                    style={{
                      width: `${(currentTime / duration) * 100}%`,
                    }}
                  />
                  <div className="absolute inset-0 cursor-not-allowed" />
                </div>
                <div className="flex justify-between text-xs sm:text-sm text-gray-600 font-medium">
                  <span>{formatTime(currentTime)}</span>
                  <span>{formatTime(duration)}</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Status Messages - Responsive */}
        {hasPlayed && (
          <div className="mt-3 sm:mt-4 p-2 sm:p-3 bg-gray-100 border border-gray-300 rounded-lg">
            <p className="text-xs sm:text-sm text-gray-700 text-center font-semibold">
              ✓ Das Audio wurde bereits vollständig abgespielt
            </p>
          </div>
        )}

        {isPlaying && !hasPlayed && (
          <div className="mt-3 sm:mt-4 p-2 sm:p-3 bg-yellow-50 border border-yellow-300 rounded-lg">
            <p className="text-xs sm:text-sm text-yellow-900 text-center font-semibold">
              ⏳ Audio läuft... Bitte warten Sie
            </p>
            <p className="text-xs text-yellow-800 text-center mt-1">
              Keine Unterbrechung möglich
            </p>
          </div>
        )}

        {!hasPlayed && !isPlaying && !hasStarted && (
          <div className="mt-3 sm:mt-4 p-2 sm:p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-xs sm:text-sm text-blue-800 text-center">
              💡 Tipp: Stellen Sie sicher, dass Sie in einer ruhigen Umgebung
              sind
            </p>
          </div>
        )}
      </div>

      {/* Hidden Audio Element */}
      <audio
        ref={audioRef}
        src={audioUrl}
        preload="metadata"
        className="hidden"
        controlsList="nodownload noplaybackrate"
        controls={false}
        onContextMenu={(e) => e.preventDefault()}
      />
    </div>
  );
};

export default HorenAudioPlayer;
